import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  User,
  MapPin,
  Phone,
  Mail,
  ChevronLeft,
  Edit,
  Trophy,
  Flag,
  AlertCircle,
  Clock,
  Award,
  Target,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useApi } from '../../contexts/ApiContext';
import { useSeries } from '../../App';

const statusColors = {
  'Active': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Current': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Inactive': 'bg-slate-100 text-slate-600 border-slate-200',
  'Retired': 'bg-red-100 text-red-700 border-red-200',
  'Suspended': 'bg-orange-100 text-orange-700 border-orange-200',
};

function parseDOB(str) {
  if (!str) return null;
  const [y, m, d] = str.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function computeAge(dobDate) {
  if (!dobDate) return null;
  const today = new Date();
  let age = today.getFullYear() - dobDate.getFullYear();
  const monthDiff = today.getMonth() - dobDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dobDate.getDate())) {
    age--;
  }
  return age;
}

function formatDOB(dobDate) {
  if (!dobDate) return null;
  return dobDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Race type colors for history
const raceTypeColors = {
  'Feature': 'text-amber-600',
  'A-Main': 'text-amber-600',
  'Heat': 'text-blue-600',
  'Qualifier': 'text-purple-600',
  'B-Main': 'text-orange-600',
};

function StatCard({ icon: Icon, label, value, subValue, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-emerald-100 text-emerald-600',
    yellow: 'bg-amber-100 text-amber-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <div className="text-2xl font-bold text-slate-800">{value}</div>
            <div className="text-sm text-slate-500">{label}</div>
            {subValue && (
              <div className="text-xs text-slate-400 mt-0.5">{subValue}</div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RaceHistoryRow({ race }) {
  const typeColor = raceTypeColors[race.race_type] || 'text-slate-500';

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
      {/* Position */}
      <div className={`w-10 h-10 flex items-center justify-center rounded-lg font-bold text-lg
        ${race.finish_position === 1 ? 'bg-amber-100 text-amber-700 border border-amber-300' :
          race.finish_position === 2 ? 'bg-slate-200 text-slate-700 border border-slate-300' :
          race.finish_position === 3 ? 'bg-orange-100 text-orange-700 border border-orange-300' :
          race.finish_position <= 5 ? 'bg-emerald-100 text-emerald-700 border border-emerald-300' :
          race.finish_position <= 10 ? 'bg-blue-100 text-blue-700 border border-blue-300' :
          'bg-slate-100 text-slate-600 border border-slate-200'}`}
      >
        {race.finish_position || '-'}
      </div>

      {/* Race Info */}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-slate-800 truncate">
          {race.race_name || race.event_name}
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span className={typeColor}>{race.race_type}</span>
          <span>•</span>
          <span>{race.track_name}</span>
        </div>
      </div>

      {/* Date */}
      <div className="text-sm text-slate-500 hidden md:block">
        {formatDate(race.event_date)}
      </div>

      {/* Points/Winnings */}
      <div className="text-right">
        {race.points > 0 && (
          <div className="text-sm font-medium text-slate-700">{race.points} pts</div>
        )}
        {race.winnings > 0 && (
          <div className="text-sm text-emerald-600">${race.winnings.toLocaleString()}</div>
        )}
      </div>
    </div>
  );
}

export default function CompetitorDetails() {
  const { seriesSlug, competitorId } = useParams();
  const api = useApi();
  const currentSeries = useSeries(); // eslint-disable-line no-unused-vars

  const { data: driverData, isLoading: loading, error: driverError } = useQuery({
    queryKey: ['driver-details', competitorId],
    queryFn: async () => {
      const [driverResult, statsResult] = await Promise.all([
        api.drivers.getById(competitorId),
        api.drivers.getStatistics(competitorId)
      ]);
      if (!driverResult.success) throw new Error(driverResult.message || 'Failed to fetch driver');
      return {
        driver: driverResult.data.driver,
        raceHistory: driverResult.data.raceHistory || [],
        stats: statsResult.success ? statsResult.data.stats : null
      };
    },
  });

  const driver = driverData?.driver || null;
  const raceHistory = driverData?.raceHistory || [];
  const stats = driverData?.stats || null;
  const error = driverError?.message || null;

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="page-header">
          <div className="h-8 w-48 bg-slate-200 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Card className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-32 bg-slate-200 rounded" />
              </CardContent>
            </Card>
          </div>
          <div className="space-y-4">
            <Card className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-48 bg-slate-200 rounded" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error || !driver) {
    return (
      <div className="animate-fade-in">
        <Card>
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-800 mb-2">
              {error || 'Driver not found'}
            </h3>
            <Link to={`/${seriesSlug}/competitors`}>
              <Button variant="ghost" leftIcon={ChevronLeft}>
                Back to Competitors
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusColor = statusColors[driver.status] || 'bg-slate-100 text-slate-600';
  // MySQL returns AVG/SUM as strings under mysql2's default config, so coerce before formatting.
  const avgFinish = stats?.avg_finish != null ? Number(stats.avg_finish) : null;
  const winPercentage = stats?.total_races > 0
    ? ((stats.wins / stats.total_races) * 100).toFixed(1)
    : '0';
  const dobDate = parseDOB(driver.date_of_birth);
  const age = computeAge(dobDate);

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center gap-4 mb-4">
          <Link to={`/${seriesSlug}/competitors`}>
            <Button variant="ghost" size="sm" leftIcon={ChevronLeft}>
              Competitors
            </Button>
          </Link>
        </div>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            {driver.headshot_url ? (
              <img
                src={driver.headshot_url}
                alt={`${driver.first_name} ${driver.last_name}`}
                className="w-20 h-20 rounded-xl object-cover bg-slate-200 border border-slate-300"
              />
            ) : (
              <div className="w-20 h-20 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center">
                <User className="w-10 h-10 text-slate-400" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="page-title">
                  {driver.first_name} {driver.last_name}
                </h1>
                {driver.status && (
                  <Badge className={statusColor}>
                    {driver.status}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-4 text-slate-500">
                {(driver.city || driver.state_code) && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" />
                    {driver.city}{driver.city && driver.state_code && ', '}{driver.state_code}
                  </span>
                )}
                {driver.team_name && (
                  <span>{driver.team_name}</span>
                )}
              </div>
              {dobDate && (
                <div className="mt-1 text-sm text-slate-500">
                  Born {formatDOB(dobDate)}
                  {age !== null && ` (age ${age})`}
                </div>
              )}
              {driver.series_names && (
                <div className="mt-2 text-sm text-slate-400">
                  {driver.series_names}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/${seriesSlug}/competitors/${competitorId}/edit`}>
              <Button variant="secondary" size="sm" leftIcon={Edit}>
                Edit
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              icon={Flag}
              label="Starts"
              value={stats?.total_races || driver.starts || 0}
              color="blue"
            />
            <StatCard
              icon={Trophy}
              label="Wins"
              value={stats?.wins || driver.wins || 0}
              subValue={`${winPercentage}% win rate`}
              color="yellow"
            />
            <StatCard
              icon={Award}
              label="Top 5s"
              value={stats?.top_5s || 0}
              color="green"
            />
            <StatCard
              icon={Target}
              label="Top 10s"
              value={stats?.top_10s || 0}
              color="purple"
            />
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Avg. Finish</div>
                <div className="text-xl font-bold text-slate-800">
                  {avgFinish != null && !Number.isNaN(avgFinish) ? avgFinish.toFixed(1) : '-'}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Total Points</div>
                <div className="text-xl font-bold text-slate-800">
                  {stats?.total_points?.toLocaleString() || '-'}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Total Earnings</div>
                <div className="text-xl font-bold text-emerald-600">
                  {stats?.total_winnings ? `$${stats.total_winnings.toLocaleString()}` : '-'}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Race History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-500" />
                Recent Race History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {raceHistory.length === 0 ? (
                <div className="text-center py-8">
                  <Flag className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No race history available</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {raceHistory.map((race, index) => (
                    <RaceHistoryRow key={race.competitor_ID || index} race={race} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Biography */}
          {driver.biography && (
            <Card>
              <CardHeader>
                <CardTitle>Biography</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 whitespace-pre-wrap">{driver.biography}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact Info */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {driver.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <a href={`mailto:${driver.email}`} className="text-rms-accent hover:underline truncate">
                    {driver.email}
                  </a>
                </div>
              )}
              {driver.mobile_phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <a href={`tel:${driver.mobile_phone}`} className="text-slate-700 hover:text-rms-accent">
                    {driver.mobile_phone}
                  </a>
                </div>
              )}
              {driver.home_phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-700">{driver.home_phone}</span>
                  <span className="text-xs text-slate-400">(Home)</span>
                </div>
              )}
              {!driver.email && !driver.mobile_phone && !driver.home_phone && (
                <p className="text-slate-400 text-sm">No contact information available</p>
              )}
            </CardContent>
          </Card>

          {/* Emergency Contact */}
          {(driver.emergency_first_name || driver.emergency_phone) && (
            <Card>
              <CardHeader>
                <CardTitle>Emergency Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {driver.emergency_first_name && (
                  <div className="text-slate-700">
                    {driver.emergency_first_name} {driver.emergency_last_name}
                    {driver.emergency_contact_relationship && (
                      <span className="text-slate-400 text-sm ml-2">
                        ({driver.emergency_contact_relationship})
                      </span>
                    )}
                  </div>
                )}
                {driver.emergency_phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <a href={`tel:${driver.emergency_phone}`} className="text-slate-700 hover:text-rms-accent">
                      {driver.emergency_phone}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Equipment */}
          {(driver.chassis || driver.team_name) && (
            <Card>
              <CardHeader>
                <CardTitle>Equipment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {driver.team_name && (
                  <div>
                    <div className="text-sm text-slate-500">Team</div>
                    <div className="text-slate-800 font-medium">{driver.team_name}</div>
                  </div>
                )}
                {driver.chassis && (
                  <div>
                    <div className="text-sm text-slate-500">Chassis</div>
                    <div className="text-slate-800 font-medium">{driver.chassis}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* External Links */}
          {(driver.merch_url || driver.spotify_url) && (
            <Card>
              <CardHeader>
                <CardTitle>Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {driver.merch_url && (
                  <a
                    href={driver.merch_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-rms-accent hover:underline"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Merchandise Store
                  </a>
                )}
                {driver.spotify_url && (
                  <a
                    href={driver.spotify_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-emerald-600 hover:underline"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Spotify Playlist
                  </a>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
